import { registerPlugin } from "@capacitor/core"

export type RuStoreProductType =
	| "NON_CONSUMABLE_PRODUCT"
	| "CONSUMABLE_PRODUCT"
	| "SUBSCRIPTION"

export type RuStorePurchaseType = "ONE_STEP" | "TWO_STEP" | "UNDEFINED"

export type RuStoreAcknowledgementState = "PENDING" | "ACKNOWLEDGED" | "UNKNOWN"

export type RuStoreSubscriptionPeriod = {
	type: "TRIAL" | "PROMO" | "MAIN" | "GRACE" | "HOLD"
	duration: string
	currency?: string
	price?: number
}

export type RuStoreProduct = {
	productId: string
	type: RuStoreProductType
	amountLabel: string
	price?: number
	currency: string
	imageUrl: string
	promoImageUrl?: string
	title: string
	description?: string
	subscriptionInfo?: { periods: RuStoreSubscriptionPeriod[] }
}

export type RuStorePurchase = {
	purchaseId: string
	invoiceId: string
	orderId?: string
	purchaseType: RuStorePurchaseType
	status: string
	description?: string
	purchaseTime?: string
	price: number
	amountLabel: string
	currency: string
	developerPayload?: string
	sandbox: boolean
	productId?: string
	productType?: RuStoreProductType
	quantity?: number
	expirationDate?: string
	gracePeriodEnabled?: boolean
	acknowledgementState?: RuStoreAcknowledgementState
}

export type RuStorePurchaseResult = {
	orderId?: string
	purchaseId: string
	productId: string
	invoiceId: string
	purchaseType: RuStorePurchaseType
	productType: RuStoreProductType
	quantity: number
	sandbox: boolean
}

/** These codes match the RuStore core errors in `ru.rustore.sdk.core.exception`. */
export type RuStoreCoreErrorCode =
	| "RUSTORE_NOT_INSTALLED"
	| "RUSTORE_OUTDATED"
	| "RUSTORE_USER_UNAUTHORIZED"
	| "RUSTORE_APP_BANNED"
	| "RUSTORE_USER_BANNED"

export type RuStorePayErrorCode =
	| RuStoreCoreErrorCode
	| "CLIENT_UNAVAILABLE"
	| "INVALID_ARGUMENT"
	| "USER_CANCELED"
	| "PURCHASE_FAILED"
	| "GET_PRODUCTS_FAILED"
	| "GET_PURCHASES_FAILED"
	| "GET_PURCHASE_FAILED"
	| "GET_AVAILABILITY_FAILED"
	| "GET_AUTHORIZATION_STATUS_FAILED"
	| "ACKNOWLEDGE_FAILED"
	| "RUSTORE_CHECK_FAILED"

/**
 * This is the `data` of a `purchase()` rejection. With `USER_CANCELED`, it has
 * only `purchaseId`. With other codes, it has the `ProductPurchaseException`
 * fields when the SDK gives them. It also has `cause`, the simple class name of
 * the SDK cause.
 */
export type RuStorePurchaseErrorData = {
	purchaseId?: string
	invoiceId?: string
	sandbox?: boolean
	cause?: string
}

export type RuStorePurchaseAvailability = {
	available: boolean
	reason?: string
	/** The plugin sets this field only when the cause is a RuStore core error. */
	reasonCode?: RuStoreCoreErrorCode
	/** This field contains the simple Java class name of the cause. */
	cause?: string
}

export type RuStoreUserAuthorizationStatus = "AUTHORIZED" | "UNAUTHORIZED"

export interface RuStorePayPlugin {
	getProducts(options: {
		productIds: string[]
	}): Promise<{ products: RuStoreProduct[] }>
	purchase(options: {
		productId: string
		appUserId?: string
		orderId?: string
	}): Promise<RuStorePurchaseResult>
	getPurchases(options?: {
		productType?: RuStoreProductType
		acknowledgementState?: RuStoreAcknowledgementState
	}): Promise<{ purchases: RuStorePurchase[] }>
	getPurchase(options: { purchaseId: string }): Promise<RuStorePurchase>
	getPurchaseAvailability(): Promise<RuStorePurchaseAvailability>
	/** A binary from before this method rejects the call with `UNIMPLEMENTED`. */
	getUserAuthorizationStatus(): Promise<{
		status: RuStoreUserAuthorizationStatus
	}>
	updateAcknowledgementState(options: {
		purchaseId: string
		state: RuStoreAcknowledgementState
	}): Promise<{ state: RuStoreAcknowledgementState }>
	isRuStoreInstalled(): Promise<{ installed: boolean }>
}

export const RuStorePay = registerPlugin<RuStorePayPlugin>("RuStorePay")
